/**
 * Envelope for year-scoped GET endpoints. The `state` discriminator picks one of
 * four shapes: present, carried-forward, inherited, absent.
 *
 * Invariants enforced by the backend:
 * - present: `data` populated, `carriedForward` is null
 * - carried-forward: `data` is null, `carriedForward.values` holds the prior-year template
 * - inherited (Details only): `data` populated, `hints.effectiveYear` set to the source year
 * - absent: `data` is null, `carriedForward` is null
 */
export type YearlyResourceState = 'present' | 'carried-forward' | 'inherited' | 'absent';

export interface CarriedForwardBlock<T> {
  sourceYear: number;
  values: T;
}

export interface YearlyHints {
  lastYearWithData?: number;
  /** Set only when state is 'inherited'; the year of the underlying record. */
  effectiveYear?: number;
  suggestedAction?: 'create' | 'review-carried' | 'edit' | 'reload-members' | 'reload-shareholders' | string;
}

export interface YearlyResource<T, C = unknown> {
  year: number;
  state: YearlyResourceState;
  data: T | null;
  carriedForward: CarriedForwardBlock<T> | null;
  context: C;
  hints: YearlyHints;
}

/**
 * True when the save dropped Director/Officer rows because the year's roster no longer holds them —
 * someone was removed from the measurement while this view held a copy loaded before the removal.
 * The rows the view sent for those members were not written; everything else in the request was.
 */
export function membersWereDropped(resource: YearlyResource<any, any> | null | undefined): boolean {
  return resource?.hints?.suggestedAction === 'reload-members';
}

/** Shared wording for the notice shown when {@link membersWereDropped} is true. */
export const MEMBERS_DROPPED_MESSAGE =
  'Some Directors/Officers are no longer part of this measurement — their values were not saved. The tables have been refreshed.';

/**
 * True when the save dropped shareholder rows because a shareholder they named no longer exists —
 * it was merged into another shareholder, or deleted, while this view held a list loaded beforehand.
 * The rows the view sent for those shareholders were not written; everything else in the request was.
 */
export function shareholdersWereDropped(resource: YearlyResource<any, any> | null | undefined): boolean {
  return resource?.hints?.suggestedAction === 'reload-shareholders';
}

/** Shared wording for the notice shown when {@link shareholdersWereDropped} is true. */
export const SHAREHOLDERS_DROPPED_MESSAGE =
  'Some shareholders no longer exist — they were merged or deleted elsewhere, so their values were not saved. The table has been refreshed.';
